import './BlockedEmailsSettings.css';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import { useEffect, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import {
    GET_BLOCKED_EMAILS,
    CREATE_BLOCKED_EMAIL,
    DELETE_BLOCKED_EMAIL,
    useLazyQuery,
    useQuery,
    useMutation,
} from 'utils/graphql';
import { isEmailValid } from 'utils/validation.util';
import { ReactComponent as CheckIcon } from 'assets/icons/check_icon.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error_icon.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-triangle.svg';
import InfoModal from 'components/Modals/InfoModal/InfoModal';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import { useTranslation } from 'react-i18next';

const BlockedEmailsSettings = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [validated, setValidated] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [isAddingEmail, setIsAddingEmail] = useState(false);

    const [searchFilter, setSearchFilter] = useState('');
    const [emailToDelete, setEmailToDelete] = useState(null);
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);

    const [emails, setEmails] = useState([]);
    const [totalEmails, setTotalEmails] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [orderBy, setOrderBy] = useState('email');
    const [orderByDirection, setOrderByDirection] = useState('asc');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [infoModalText, setInfoModalText] = useState('');
    const [infoModalOptions, setInfoModalOptions] = useState({});
    const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);

    const firstUpdate = useRef(true);

    const { refetch: getEmails } = useQuery(GET_BLOCKED_EMAILS, {
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            if (data.table) {
                setTotalEmails(data.table.count);
                setEmails(
                    data.table.records.map((record) => {
                        return { ...record.blocked, isDeleting: false };
                    })
                );
                if (firstUpdate.current) {
                    firstUpdate.current = false;
                }
                setIsLoading(false);
            }
        },
        onError: (error) => {
            console.log(error);
        },
    });

    const [createBlockedEmail] = useMutation(CREATE_BLOCKED_EMAIL);
    const [deleteBlockedEmailMutation] = useMutation(DELETE_BLOCKED_EMAIL);

    const [getBlockedEmailByName] = useLazyQuery(GET_BLOCKED_EMAILS, {
        fetchPolicy: 'cache-and-network',
    });

    const columns = [
        {
            name: t('email'),
            sortable: true,
            id: 'email',
            selector: (row) => row.email,
        },
        {
            name: t('delete'),
            sortable: false,
            id: 'manage',
            width: '200px',
            selector: (row) => (
                <div className="d-flex align-items-center">
                    <LoadingButton
                        isLoading={row.isDeleting}
                        isDisabled={deletionInProgress() || isAddingEmail}
                        variant="danger"
                        onClick={() => {
                            setEmailToDelete(row);
                            setIsDeleteConfirmationModalOpen(true);
                        }}
                    >
                        {t('delete')}
                    </LoadingButton>
                </div>
            ),
        },
    ];

    const showInfoModal = (text, success = true) => {
        setInfoModalText(text);
        setInfoModalOptions({
            header: success ? (
                <CheckIcon fill="var(--bs-green)" />
            ) : (
                <ErrorIcon fill="var(--bs-red)" />
            ),
            center: true,
        });
        setInfoModalIsOpen(true);
    };

    const deletionInProgress = () => {
        return emails.some((email) => email.isDeleting);
    };

    const datatableOnSort = (selectedColumn, sortDirection, sortedRows) => {
        if (selectedColumn && selectedColumn.id) {
            setOrderBy(selectedColumn.id);
            setOrderByDirection(sortDirection);
        }
    };

    const addBlockedEmail = async () => {
        setIsAddingEmail(true);
        if (await validate()) {
            const { data, errors } = await createBlockedEmail({
                variables: {
                    email,
                },
            });
            if (errors) {
                showInfoModal('blocked_email_add_fail');
            } else {
                showInfoModal('blocked_email_add_success');
                loadEmails();
                setEmail('');
            }
            setIsAddingEmail(false);
        } else {
            setIsAddingEmail(false);
        }
    };

    const deleteBlockedEmail = async () => {
        emailToDelete.isDeleting = true;
        const { data, errors } = await deleteBlockedEmailMutation({
            variables: {
                id: emailToDelete.id,
            },
        });
        emailToDelete.isDeleting = false;
        if (errors) {
            showInfoModal('blocked_email_delete_fail');
        } else {
            showInfoModal('blocked_email_delete_success');
            loadEmails();
        }
    };

    const validate = async () => {
        setEmailError('');
        let errors = false;
        if (!email) {
            setEmailError('email_empty_error');
            errors = true;
        } else if (!isEmailValid(email)) {
            setEmailError('valid_email_error');
            errors = true;
        }

        if (errors) {
            setValidated(true);
            return false;
        }

        const { data } = await getBlockedEmailByName({
            variables: {
                filter: email,
            },
        });
        if (data && data.table.count > 0) {
            setEmailError('email_already_blocked');
            setValidated(true);
            return false;
        }

        return true;
    };

    const loadEmails = () => {
        setIsLoading(true);
        getEmails({
            offset: (page - 1) * rowsPerPage,
            limit: rowsPerPage,
            orderBy,
            orderByDirection: orderByDirection.toUpperCase(),
            filter: searchFilter ? `%${searchFilter}%` : '%',
        });
    };

    useEffect(() => {
        if (firstUpdate.current) return;
        loadEmails();
    }, [page, rowsPerPage, orderBy, orderByDirection]);

    useEffect(() => {
        if (firstUpdate.current) return;
        const timer = setTimeout(loadEmails, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [searchFilter]);

    useEffect(() => setValidated(false), [email]);

    return (
        <div className="blocked-emails-settings">
            <Form.Group className="mb-3">
                <Form.Control
                    type="text"
                    isInvalid={validated && emailError}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('email_to_block')}
                />
                {emailError && (
                    <Form.Control.Feedback type="invalid">{t(emailError)}</Form.Control.Feedback>
                )}
            </Form.Group>
            <LoadingButton
                className="w-100 mb-4"
                maxWidth="100%"
                isLoading={isAddingEmail}
                isDisabled={deletionInProgress()}
                onClick={addBlockedEmail}
            >
                {t('add')}
            </LoadingButton>
            <div className="d-flex justify-content-end">
                <Form.Control
                    type="text"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    placeholder={t('search')}
                    className="search"
                />
            </div>
            <DataTable
                columns={columns}
                data={emails}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                paginationTotalRows={totalEmails}
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage) => setRowsPerPage(currentRowsPerPage)}
                progressPending={isLoading}
                progressComponent={
                    <div className="d-flex justify-content-center align-items-center my-4">
                        <Spinner></Spinner>
                    </div>
                }
                highlightOnHover
                persistTableHead
                sortServer
                onSort={(selectedColumn, sortDirection, sortedRows) =>
                    datatableOnSort(selectedColumn, sortDirection, sortedRows)
                }
                paginationComponentOptions={{ rowsPerPageText: t('rows_per_page') }}
            />
            <InfoModal
                isOpen={infoModalIsOpen}
                setOpen={setInfoModalIsOpen}
                options={infoModalOptions}
            >
                {t(infoModalText)}
            </InfoModal>
            <ConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                setOpen={setIsDeleteConfirmationModalOpen}
                onConfirm={deleteBlockedEmail}
                options={{
                    center: true,
                    header: <WarningIcon fill="var(--bs-yellow)" height={48} width={48} />,
                }}
            >
                <p>{t('delete_blocked_email_confirmation')}</p>
            </ConfirmationModal>
        </div>
    );
};

export default BlockedEmailsSettings;
